/**
 * Formatting utilities for Frontend/Homepage module
 *
 * Pure functions for formatting prices, text, and other display values.
 * Matches CardItem.tsx patterns exactly.
 */

import { getBaseUrl } from './baseUrl'
import {
  getMagentoProductUrl,
  getMagentoProductImageUrl,
  getMagentoProductHoverImageUrl,
  getMagentoMsrp,
  isMagentoProductNew,
  hasMagentoProductVideo,
} from './productHelpers'

import type {
  Banner,
  HomepageBanner,
  Product,
  MagentoProduct,
} from '../types'

/** Valid Magento product types */
const VALID_PRODUCT_TYPES = ['simple', 'configurable', 'grouped', 'bundle', 'virtual', 'downloadable'] as const
type ValidProductType = (typeof VALID_PRODUCT_TYPES)[number]

/** Type guard for valid product types */
function isValidProductType(value: unknown): value is ValidProductType {
  return typeof value === 'string' && VALID_PRODUCT_TYPES.includes(value as ValidProductType)
}

/**
 * Truncate text to max length with ellipsis
 * Default max is 70 chars (matches CardItem.tsx)
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 70)
 * @returns Truncated text with "..." if needed
 */
export function truncateText(text: string, maxLength = 70): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength - 3)}...`
}

/**
 * Truncate product name (alias for truncateText with 70 char default)
 * Matches CardItem.tsx truncation logic
 */
export function truncateName(name: string): string {
  return truncateText(name, 70)
}

// =============================================================================
// HOMEPAGE TRANSFORMERS
// =============================================================================

/**
 * Transform HomepageBanner (snake_case) to Banner block type (camelCase)
 *
 * Used by HomepageSections to convert domain types to reusable block types.
 *
 * @param banner - HomepageBanner from GraphQL response
 * @returns Banner for BannerSlider component
 */
export function transformBannerToBannerBlock(banner: HomepageBanner): Banner {
  return {
    id: banner.id,
    title: banner.title ?? undefined,
    imageDesktop: banner.image_desktop ?? undefined,
    imageMobile: banner.image_mobile ?? undefined,
    link: banner.link ?? undefined,
    linkText: banner.link_text ?? undefined,
  }
}

/**
 * Transform array of HomepageBanners to Banner block types
 *
 * @param banners - Array of HomepageBanner from GraphQL
 * @returns Array of Banner for BannerSlider
 */
export function transformBannersToBannerBlock(banners: HomepageBanner[]): Banner[] {
  return banners.map(transformBannerToBannerBlock)
}

/**
 * Transform MagentoProduct (standard products query) to ProductCard Product type
 *
 * Uses price_range from standard Magento ProductInterface.
 * Used by ProductSlider to convert Magento products to ProductCard format.
 *
 * @param mp - MagentoProduct from standard Magento products query
 * @returns Product for ProductCard component
 */
export function transformMagentoProductToProduct(mp: MagentoProduct): Product {
  const baseUrl = getBaseUrl()

  const regularPrice = mp.price_range.minimum_price.regular_price.value
  const finalPrice = mp.price_range.minimum_price.final_price.value
  const msrp = getMagentoMsrp(mp)

  // Get relative URL from canonical_url or build from url_key + url_suffix
  const productPath = getMagentoProductUrl(mp)
  // Remove leading slash if baseUrl is empty (relative URLs)
  const url = baseUrl ? `${baseUrl}${productPath}` : productPath

  return {
    id: mp.sku,
    sku: mp.sku,
    name: mp.name,
    url,
    imageUrl: getMagentoProductImageUrl(mp) ?? undefined,
    hoverImageUrl: getMagentoProductHoverImageUrl(mp),
    price: regularPrice,
    // Discount exists when final_price < regular_price
    specialPrice: finalPrice < regularPrice ? finalPrice : undefined,
    msrp,
    productType: isValidProductType(mp.type_id) ? mp.type_id : undefined,
    isNew: isMagentoProductNew(mp),
    hasVideo: hasMagentoProductVideo(mp),
  }
}

/**
 * Transform array of MagentoProduct to ProductCard Product array
 */
export function transformMagentoProductsToProducts(products: MagentoProduct[]): Product[] {
  return products.map(transformMagentoProductToProduct)
}
